import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import app from './firebaseConfig';
import MapViewer from './MapViewer';
import AdminDashboard from './AdminDashboard';
import ApprovalCelebrationModal from './ApprovalCelebrationModal';
import { getFirestore, doc, getDoc } from 'firebase/firestore';

import { useTheme } from './hooks/useTheme';
import { AuthProvider } from './AuthContext';
import './index.css';

function App() {
  const [rinks, setRinks] = useState([]);
  const [trails, setTrails] = useState([]);
  const [viewType, setViewType] = useState('rink');
  const [approvedSuggestions, setApprovedSuggestions] = useState([]);
  const [showCelebration, setShowCelebration] = useState(false);
  const apiKey = 'AIzaSyCY7tnMWTt69m0AsmBNjpcJnwoMFylPJxI';
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    // Function to fetch rinks data from Firestore
    const fetchRinks = async () => {
      const db = getFirestore(app);
      const docRef = doc(db, "aggregatedrinks", "data");
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        console.log("Rinks data:", docSnap.data());
        // Approved overrides are already in the aggregate data
        // No need to merge - saves tons of Firestore reads!
        setRinks(docSnap.data().patinoire);
      }
    };

    // Function to fetch ski trails data from Firestore
    const fetchTrails = async () => {
      const db = getFirestore(app);
      const docRef = doc(db, "aggregatedskitrails", "data");
      try {
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          console.log("Trails data:", docSnap.data());
          const docData = docSnap.data();
          // Try to find the correct array field for trails
          if (docData.piste) {
            setTrails(docData.piste);
          } else if (docData.trails) {
            setTrails(docData.trails);
          } else if (docData.patinoire) {
            // Fallback if they reused the structure name
            setTrails(docData.patinoire);
          } else {
            // Fallback: use the first array value found in the object
            const firstArray = Object.values(docData).find(val => Array.isArray(val));
            if (firstArray) {
              setTrails(firstArray);
            } else {
              console.warn("No array found in ski trails document");
            }
          }
        }
      } catch (error) {
        console.error("Error fetching trails:", error);
      }
    };

    fetchRinks();
    fetchTrails();
  }, []);

  // Check for approved suggestions
  useEffect(() => {
    const checkApprovedSuggestions = async () => {
      const userId = localStorage.getItem('anonymousUserId');
      if (!userId) return;

      try {
        const db = getFirestore(app);
        const { getDoc: getDocImport, doc: docImport } = await import('firebase/firestore');
        const userDocRef = docImport(db, 'user_contributions', userId);
        const userDoc = await getDocImport(userDocRef);

        if (userDoc.exists()) {
          const contributions = userDoc.data().contributions || [];

          // Filter for approved but not yet acknowledged contributions
          const newlyApproved = contributions.filter(c =>
            c.status === 'approved' &&
            c.type === 'location' &&
            !c.acknowledgedAt
          );

          if (newlyApproved.length > 0) {
            setApprovedSuggestions(newlyApproved);
            setShowCelebration(true);
          }
        }
      } catch (error) {
        console.error('Error checking approved suggestions:', error);
      }
    };

    if (rinks.length > 0) {
      checkApprovedSuggestions();
    }
  }, [rinks]);

  const currentData = viewType === 'trail' ? trails : rinks;

  // Handler for view type change, passed to MapViewer
  const handleViewTypeChange = (type) => {
    setViewType(type);
  };

  return (
    <AuthProvider>
      <Router>
        <div className="app" data-theme={theme}>
          <Routes>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/" element={
              rinks.length > 0 || trails.length > 0 ? (
                <MapViewer
                  rinks={currentData}
                  apiKey={apiKey}
                  theme={theme}
                  toggleTheme={toggleTheme}
                  viewType={viewType}
                  onViewTypeChange={handleViewTypeChange}
                />
              ) : (
                <div className="loading-message">Chargement des données...</div>
              )
            } />
          </Routes>

          {/* Celebration modal for approved suggestions */}
          {showCelebration && approvedSuggestions.length > 0 && (
            <ApprovalCelebrationModal
              approvedSuggestions={approvedSuggestions}
              onDismiss={() => setShowCelebration(false)}
            />
          )}
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
